import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import SeoHead from "../components/seo/SeoHead";
import API from "../services/api";

const THEME_KEY = "hookora_theme";

function MilestoneWatchPage() {
  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const storedUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const token =
    typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  const isAdmin = Boolean(storedUser?.is_admin);

  const [data, setData] = useState(null);
  const [displayCount, setDisplayCount] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [isCelebrating, setIsCelebrating] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme());

  const previousCountRef = useRef(null);
  const celebrationTimeoutRef = useRef(null);
  const isRequestInFlightRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      pageBg: isDark ? "#020617" : "#f8fafc",
      pageGradient: isDark
        ? isCelebrating
          ? "radial-gradient(circle at center, rgba(56,189,248,0.25), rgba(34,197,94,0.18), #020617 65%)"
          : "linear-gradient(135deg, #020617, #1e1b4b, #312e81)"
        : isCelebrating
        ? "radial-gradient(circle at center, rgba(56,189,248,0.10), rgba(34,197,94,0.08), #f8fafc 70%)"
        : "radial-gradient(circle at top left, rgba(37,99,235,0.10), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.08), transparent 24%), #f8fafc",
      text: isDark ? "#ffffff" : "#111827",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      textMuted: isDark ? "#c7d2fe" : "#6366f1",
      textSubtle: isDark ? "#e5e7eb" : "#6b7280",
      panelBg: isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.78)",
      panelBorder: isDark ? "rgba(255,255,255,0.12)" : "#e5e7eb",
      panelShadow: isDark
        ? "0 20px 50px rgba(0,0,0,0.25)"
        : "0 12px 30px rgba(15, 23, 42, 0.08)",
      heroBg: isDark
        ? isCelebrating
          ? "rgba(255,255,255,0.12)"
          : "rgba(255,255,255,0.08)"
        : isCelebrating
        ? "rgba(255,255,255,0.92)"
        : "rgba(255,255,255,0.86)",
      heroBorder: isDark
        ? isCelebrating
          ? "1px solid rgba(103,232,249,0.35)"
          : "1px solid rgba(255,255,255,0.12)"
        : isCelebrating
        ? "1px solid rgba(56,189,248,0.28)"
        : "1px solid #e5e7eb",
      heroShadow: isDark
        ? isCelebrating
          ? "0 0 0 1px rgba(103,232,249,0.15), 0 0 40px rgba(34,197,94,0.25), 0 0 90px rgba(56,189,248,0.22)"
          : "0 20px 50px rgba(0,0,0,0.25)"
        : isCelebrating
        ? "0 0 0 1px rgba(56,189,248,0.10), 0 16px 42px rgba(34,197,94,0.12)"
        : "0 18px 44px rgba(15, 23, 42, 0.08)",
      cardBg: isDark ? "rgba(255,255,255,0.08)" : "#ffffff",
      cardBorder: isDark ? "rgba(255,255,255,0.12)" : "#e5e7eb",
      buttonGhostBg: isDark ? "rgba(255,255,255,0.14)" : "#eef2ff",
      buttonGhostText: isDark ? "#ffffff" : "#312e81",
      buttonPrimaryBg: isDark ? "#ffffff" : "#111827",
      buttonPrimaryText: isDark ? "#111827" : "#ffffff",
      progressTrack: isDark ? "rgba(255,255,255,0.12)" : "#e5e7eb",
      errorBg: isDark ? "rgba(239, 68, 68, 0.15)" : "#fef2f2",
      errorBorder: isDark ? "rgba(248, 113, 113, 0.4)" : "#fecaca",
      errorText: isDark ? "#fecaca" : "#b91c1c",
      celebrateText: isDark ? "#86efac" : "#15803d"
    }),
    [isDark, isCelebrating]
  );

  const triggerCelebration = () => {
    setIsCelebrating(true);

    if (celebrationTimeoutRef.current) {
      clearTimeout(celebrationTimeoutRef.current);
    }

    celebrationTimeoutRef.current = setTimeout(() => {
      setIsCelebrating(false);
    }, 2500);
  };

  const animateCount = (start, end) => {
    const duration = 700;
    const startTime = performance.now();

    const step = (currentTime) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = Math.round(start + (end - start) * eased);

      setDisplayCount(nextValue);

      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };

    window.requestAnimationFrame(step);
  };

  const loadMilestoneData = async () => {
    if (isRequestInFlightRef.current) return;

    try {
      isRequestInFlightRef.current = true;

      const response = await API.get("/waitlist/admin/milestone-watch");
      const nextData = response.data;
      const nextCount = nextData?.total_users ?? 0;

      setData(nextData);
      setError("");

      if (previousCountRef.current === null) {
        previousCountRef.current = nextCount;
        setDisplayCount(nextCount);
        return;
      }

      const previousCount = previousCountRef.current;

      if (nextCount !== previousCount) {
        animateCount(previousCount, nextCount);

        if (nextCount > previousCount) {
          triggerCelebration();
        }

        previousCountRef.current = nextCount;
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load milestone data.");
    } finally {
      setLoading(false);
      isRequestInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!token || !isAdmin) return;

    loadMilestoneData();
    const interval = setInterval(loadMilestoneData, 1000);

    return () => clearInterval(interval);
  }, [token, isAdmin]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      if (celebrationTimeoutRef.current) {
        clearTimeout(celebrationTimeoutRef.current);
      }
    };
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error("Fullscreen toggle failed:", err);
    }
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: colors.pageBg,
        backgroundImage: colors.pageGradient,
        color: colors.text,
        transition: "background 0.6s ease"
      }}
    >
      <SeoHead
        title="Milestone Watch | Hookora Admin"
        description="Live milestone watch page for Hookora admin signup growth."
        path="/admin/milestone-watch"
      />

      <Navbar />

      <div
        style={{
          maxWidth: "1500px",
          margin: "0 auto",
          padding: "24px 16px 40px"
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
            marginBottom: "24px"
          }}
        >
          <div>
            <div style={{ fontSize: "14px", color: colors.textMuted, fontWeight: 700 }}>
              ADMIN
            </div>
            <h1 style={{ margin: "8px 0 0", fontSize: "34px", lineHeight: 1.1 }}>
              Hookora Milestone Watch
            </h1>
          </div>

          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap"
            }}
          >
            <button
              onClick={toggleFullscreen}
              style={{
                border: "none",
                background: colors.buttonGhostBg,
                color: colors.buttonGhostText,
                padding: "12px 16px",
                borderRadius: "12px",
                fontWeight: "700",
                cursor: "pointer"
              }}
            >
              {isFullscreen ? "Exit Fullscreen" : "Fullscreen Mode"}
            </button>

            <Link
              to="/admin/waitlist"
              style={{
                textDecoration: "none",
                color: colors.buttonPrimaryText,
                background: colors.buttonPrimaryBg,
                padding: "12px 16px",
                borderRadius: "12px",
                fontWeight: "700"
              }}
            >
              Back to Waitlist Admin
            </Link>
          </div>
        </div>

        {loading ? (
          <div style={{ fontSize: "18px", color: colors.textSubtle }}>
            Loading milestone data...
          </div>
        ) : error ? (
          <div
            style={{
              background: colors.errorBg,
              border: `1px solid ${colors.errorBorder}`,
              color: colors.errorText,
              borderRadius: "16px",
              padding: "18px"
            }}
          >
            {error}
          </div>
        ) : (
          <>
            <div
              style={{
                background: colors.heroBg,
                border: colors.heroBorder,
                borderRadius: "32px",
                padding: "36px 28px 42px",
                boxShadow: colors.heroShadow,
                textAlign: "center",
                transform: isCelebrating ? "scale(1.01)" : "scale(1)",
                transition: "all 0.5s ease",
                backdropFilter: isDark ? "blur(14px)" : "blur(8px)"
              }}
            >
              <div
                style={{
                  color: colors.textMuted,
                  fontSize: "clamp(20px, 2vw, 28px)",
                  fontWeight: 800,
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                  marginBottom: "18px"
                }}
              >
                Total Users
              </div>

              <div
                style={{
                  fontSize: "clamp(110px, 22vw, 260px)",
                  lineHeight: 0.9,
                  fontWeight: 900,
                  letterSpacing: "-0.06em",
                  margin: "0 auto 26px",
                  textAlign: "center",
                  textShadow: isCelebrating
                    ? isDark
                      ? "0 0 18px rgba(255,255,255,0.25), 0 0 45px rgba(56,189,248,0.32)"
                      : "0 0 12px rgba(56,189,248,0.16)"
                    : "none",
                  transition: "text-shadow 0.5s ease"
                }}
              >
                {displayCount}
              </div>

              {isCelebrating && (
                <div
                  style={{
                    marginBottom: "18px",
                    color: colors.celebrateText,
                    fontSize: "20px",
                    fontWeight: 800,
                    letterSpacing: "0.02em"
                  }}
                >
                  New signup detected 🎉
                </div>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "14px",
                  marginTop: "18px"
                }}
              >
                <StatCard label="New in 24h" value={data?.new_users_24h ?? 0} colors={colors} />
                <StatCard label="New in 7d" value={data?.new_users_7d ?? 0} colors={colors} />
                <StatCard label="Paid Users" value={data?.paid_users ?? 0} colors={colors} />
                <StatCard label="Next Milestone" value={data?.next_milestone ?? "—"} colors={colors} />
              </div>

              <div style={{ marginTop: "28px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    marginBottom: "10px",
                    color: colors.textSubtle,
                    fontWeight: 600,
                    flexWrap: "wrap"
                  }}
                >
                  <span>Progress to {data?.next_milestone ?? "Maxed"}</span>
                  <span>{data?.users_to_next_milestone ?? 0} users to go</span>
                </div>

                <div
                  style={{
                    width: "100%",
                    height: "22px",
                    background: colors.progressTrack,
                    borderRadius: "999px",
                    overflow: "hidden"
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(data?.progress_percent ?? 0, 100)}%`,
                      height: "100%",
                      background: "linear-gradient(90deg, #22c55e, #38bdf8, #a78bfa)",
                      borderRadius: "999px",
                      transition: "width 0.6s ease"
                    }}
                  />
                </div>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: "16px",
                marginTop: "22px"
              }}
            >
              <InfoPanel
                title="Latest Signup"
                colors={colors}
                body={
                  data?.latest_signup ? (
                    <>
                      <div style={{ fontWeight: 700, fontSize: "18px" }}>
                        {data.latest_signup.username}
                      </div>
                      <div style={{ color: colors.textSoft, marginTop: "6px" }}>
                        {data.latest_signup.email}
                      </div>
                      <div style={{ color: colors.textMuted, marginTop: "10px" }}>
                        Plan: {data.latest_signup.plan || "free"}
                      </div>
                    </>
                  ) : (
                    "No signup data yet."
                  )
                }
              />

              <InfoPanel
                title="Paid Breakdown"
                colors={colors}
                body={
                  <>
                    <div style={{ marginBottom: "8px" }}>Creator: {data?.creator_users ?? 0}</div>
                    <div>Pro: {data?.pro_users ?? 0}</div>
                  </>
                }
              />

              <InfoPanel
                title="Live Status"
                colors={colors}
                body="This page refreshes every second for near real-time milestone tracking."
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, colors }) {
  return (
    <div
      style={{
        background: colors.cardBg,
        border: `1px solid ${colors.cardBorder}`,
        borderRadius: "18px",
        padding: "18px",
        backdropFilter: "blur(8px)"
      }}
    >
      <div style={{ color: colors.textMuted, fontSize: "14px", fontWeight: 700 }}>
        {label}
      </div>
      <div
        style={{
          marginTop: "8px",
          fontSize: "36px",
          fontWeight: 800,
          color: colors.text
        }}
      >
        {value}
      </div>
    </div>
  );
}

function InfoPanel({ title, body, colors }) {
  return (
    <div
      style={{
        background: colors.panelBg,
        border: `1px solid ${colors.panelBorder}`,
        borderRadius: "20px",
        padding: "20px",
        boxShadow: colors.panelShadow,
        backdropFilter: "blur(10px)"
      }}
    >
      <div
        style={{
          color: colors.textMuted,
          fontSize: "14px",
          fontWeight: 700,
          marginBottom: "10px"
        }}
      >
        {title}
      </div>
      <div style={{ color: colors.text, lineHeight: 1.7 }}>{body}</div>
    </div>
  );
}

export default MilestoneWatchPage;